import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { WelcomeConfetti } from '../components/WelcomeConfetti';
import { CheckCircle2, AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paypalToken = searchParams.get('token');
  const paymentMethod = searchParams.get('method');
  const [status, setStatus] = useState('loading');
  const [showConfetti, setShowConfetti] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const checkPaymentStatus = useCallback(async () => {
    const checkId = sessionId || paypalToken;
    if (!checkId) {
      setStatus('error');
      return;
    }

    try {
      // For PayPal, capture the order first
      if (paymentMethod === 'paypal' && paypalToken && pollCount === 0) {
        await axios.post(
          `${API_URL}/api/payments/paypal/capture/${paypalToken}`,
          {},
          { withCredentials: true }
        );
      }

      const { data } = await axios.get(
        `${API_URL}/api/payments/status/${checkId}`,
        { withCredentials: true }
      );

      if (data.payment_status === 'paid') {
        setStatus('success');
        setShowConfetti(true);
        await refreshUser();
      } else if (data.status === 'expired') {
        setStatus('error');
      } else if (pollCount < 5) {
        setPollCount(prev => prev + 1);
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('Payment status check error:', err);
      if (pollCount < 5) {
        setPollCount(prev => prev + 1);
      } else {
        setStatus('error');
      }
    }
  }, [sessionId, paypalToken, paymentMethod, pollCount, refreshUser]);

  useEffect(() => {
    if (status === 'loading' && pollCount < 5) {
      const timer = setTimeout(checkPaymentStatus, pollCount === 0 ? 0 : 2000);
      return () => clearTimeout(timer);
    }
  }, [status, pollCount, checkPaymentStatus]);

  const handleContinue = () => {
    navigate('/dashboard/admin');
  };

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <WelcomeConfetti show={showConfetti} />
      
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-card flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#FF6F61] animate-spin" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
                Processing Payment...
              </h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your payment
              </p>
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6 welcome-animation">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#A8E6CF]/20 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-[#A8E6CF]" />
            </div>
            
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-4">
                Congratulations on your successful registration!
              </h1>
              <div className="flex items-center justify-center gap-2 mb-4">
                <img src="https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png" alt="FendanaCura" className="h-6 w-auto" />
                <span className="text-xl font-heading font-semibold text-[#FF6F61]">
                  Welcome to FendanaCura
                </span>
              </div>
              <p className="text-lg text-muted-foreground italic">
                Smart care, seamlessly managed
              </p>
            </div>

            <div className="p-6 rounded-xl bg-card border border-border text-left space-y-4">
              <h3 className="text-lg font-heading font-semibold text-foreground">What happens next?</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#A8E6CF] mt-0.5 flex-shrink-0" />
                  <span>Your agency has been registered with a unique CISC code</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#A8E6CF] mt-0.5 flex-shrink-0" />
                  <span>An invitation has been sent to your CEO/Principal Admin</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#A8E6CF] mt-0.5 flex-shrink-0" />
                  <span>You can start inviting caregivers and clients</span>
                </li>
              </ul>
            </div>

            <Button
              onClick={handleContinue}
              className="btn-primary w-full py-6"
              data-testid="continue-to-dashboard-btn"
            >
              Continue to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
                Payment Issue
              </h1>
              <p className="text-muted-foreground">
                We couldn't confirm your payment. Please try again or contact support.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/register-company')}
                className="btn-primary w-full"
                data-testid="try-again-btn"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="btn-secondary w-full"
              >
                Return Home
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentSuccess;
